import React, { useState } from 'react';
import { HandHistoryCard } from '../../app/components/HandHistoryCard';

interface CoachingBubbleProps {
  text: string | null;
  street: string; // e.g., "preflop", "flop"
  isVisible: boolean;
  // Add new props for the tabular view
  streetAnalysis?: {
    rating?: number;
    analysis?: string;
    learningTips?: string[];
    opponentRangeEstimation?: {
      likelyHands?: string[];
      handClassSummary?: string;
    };
    gtoOptimalPlay?: Array<{ action: string; frequency: number }>; // GTO frequency mix
    // NEW: deterministic/LLM-provided postflop stats
    heroOuts?: number;
    heroEquityPct?: number;
    callProfitability?: {
      considered?: boolean;
      amountToCall?: number;
      potBeforeDecision?: number;
      requiredEquityPct?: number;
      potOddsPct?: number;
      potOddsRatio?: string;
      heroEquityPctUsed?: number;
      profitable?: boolean;
      marginPct?: number;
    };
  } | null;
  keyMistakes?: Array<{ street: string; description: string }> | null;
  // New prop to indicate if it's in drawer context
  isInDrawer?: boolean;
  // NEW: optional street tabs to allow switching streets directly from bubble
  tabs?: Array<{ key: string; label: string; disabled?: boolean }>;
  activeTabKey?: string;
  onTabSelect?: (key: string) => void;
  showChatButton?: boolean;
  onChatWithCoach?: () => void;
}

const CoachingBubble: React.FC<CoachingBubbleProps> = ({ 
  text, 
  street, 
  isVisible, 
  streetAnalysis, 
  keyMistakes,
  isInDrawer = false,
  tabs,
  activeTabKey,
  onTabSelect,
  showChatButton = false,
  onChatWithCoach
}) => {
  const [showAlternative, setShowAlternative] = useState(false);
  
  if (!isVisible) {
    return null;
  }

  // Find any key mistakes for this street
  const streetMistakes = keyMistakes?.filter(mistake => mistake.street.toLowerCase() === street.toLowerCase());
  
  // Check if we have learning tips to show
  const hasAlternative = streetAnalysis?.learningTips && streetAnalysis.learningTips.length > 0;

  // Robust parse: support "AsKd", "AK", "AKs", allow spaces; fallback placeholders
  type ParsedCard = { rank?: string; suit?: string; valid: boolean; suitKnown: boolean };
  const parseLikelyHand = (hand: string | undefined): ParsedCard[] => {
    if (!hand) return [{ valid: false, suitKnown: false }, { valid: false, suitKnown: false }];
    const raw = hand.replace(/\s+/g, '').toLowerCase();
    // Full specific: rank+suit rank+suit
    if (/^[2-9tjqka][hdcs][2-9tjqka][hdcs]$/.test(raw)) {
      return [
        { rank: raw[0], suit: raw[1], valid: true, suitKnown: true },
        { rank: raw[2], suit: raw[3], valid: true, suitKnown: true },
      ];
    }
    // Rank-only shorthand, possibly with suited/offsuit marker at end (e.g., AK, AKs, AKo)
    if (/^[2-9tjqka]{2}[so]?$/.test(raw)) {
      return [
        { rank: raw[0], valid: true, suitKnown: false },
        { rank: raw[1], valid: true, suitKnown: false },
      ];
    }
    // Unknown format -> two invalid placeholders
    return [{ valid: false, suitKnown: false }, { valid: false, suitKnown: false }];
  };

  // Mini card renderer for fallback states
  const MiniFallbackCard: React.FC<{ label: string; muted?: boolean }> = ({ label, muted }) => (
    <div style={{
      width: 18,
      height: 24,
      borderRadius: 3,
      border: '1px solid #d1d5db',
      background: '#fff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      color: muted ? '#6b7280' : '#111827'
    }}>
      {label}
    </div>
  );

  // Helpers for consistent formatting/alignment of call profitability row
  const formatPercent = (value: number | undefined | null): string => {
    if (value === undefined || value === null || Number.isNaN(value as any)) return '–';
    const num = Number(value);
    // Accept either 0-1 fraction or 0-100 percent
    const pct = num <= 1 && num >= 0 ? num * 100 : num;
    const rounded = Math.round(pct * 10) / 10;
    return `${rounded}%`;
  };

  const computePotOddsRatio = (): string | null => {
    const cp = streetAnalysis?.callProfitability;
    if (!cp) return null;
    if (typeof cp.potOddsRatio === 'string' && cp.potOddsRatio.trim()) return cp.potOddsRatio;
    const toCall = typeof cp.amountToCall === 'number' ? cp.amountToCall : NaN;
    const pot = typeof cp.potBeforeDecision === 'number' ? cp.potBeforeDecision : NaN;
    if (!Number.isFinite(toCall) || !Number.isFinite(pot) || toCall <= 0) return null;
    // Typical display as Pot:Call => (pot / toCall):1
    const ratio = pot / toCall;
    const rounded = Math.round(ratio * 10) / 10;
    return `${rounded}:1`;
  };

  const renderTabs = () => {
    if (!tabs || tabs.length === 0) return null;
    const rating = typeof streetAnalysis?.rating === 'number' ? streetAnalysis!.rating as number : undefined;
    let category = '' as string;
    let bg = '' as string;
    if (typeof rating === 'number') {
      category = rating >= 9 ? 'Excellent' : rating >= 7 ? 'Good' : rating >= 4 ? 'Okay' : 'Poor';
      bg = rating >= 9 ? '#10b981' : rating >= 7 ? '#3b82f6' : rating >= 4 ? '#f59e0b' : '#ef4444';
    }
    return (
      <div style={{ width: '100%', marginBottom: 6 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(' + tabs.length + ', 1fr)', gap: 6, width: '100%', marginBottom: rating !== undefined ? 4 : 0 }}>
          {tabs.map(t => {
            const active = (activeTabKey || street) === t.key;
            return (
              <button
                key={t.key}
                type="button"
                disabled={t.disabled}
                onClick={() => onTabSelect && onTabSelect(t.key)}
                style={{
                  fontSize: 12,
                  padding: '8px 0',
                  borderRadius: 8,
                  border: '1px solid ' + (active ? '#1E40AF' : '#d1d5db'),
                  background: active ? '#E5EDFF' : '#ffffff',
                  color: active ? '#1E40AF' : '#374151',
                  cursor: t.disabled ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {rating !== undefined && (
          <div style={{
            width: '100%',
            borderRadius: 8,
            background: bg,
            color: 'white',
            padding: '4px 8px',
            fontSize: 11,
            fontWeight: 800,
            textAlign: 'center'
          }}>
            {`${category} (${rating}/10)`}
          </div>
        )}
      </div>
    );
  };

  // Animated Chippy Icon with talking facial features
  const ChippyIcon = () => {
    // Determine if Chippy should be sad based on rating
    const isLowRating = streetAnalysis?.rating !== undefined && streetAnalysis.rating <= 4;
    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        marginRight: '8px', 
        flexShrink: 0 
      }}>
        <div style={{
          position: 'relative',
          width: '32px',
          height: '32px'
        }}>
          {/* Animated Chippy SVG */}
          <svg 
            width="32" 
            height="32" 
            viewBox="-100 -100 7483.6 7483.6" 
            style={{ 
              borderRadius: '50%',
              shapeRendering: 'geometricPrecision',
              imageRendering: 'crisp-edges',
              vectorEffect: 'non-scaling-stroke'
            }}
          >
            <defs>
              <style>{`
                .st0{fill:#F0DCC4;}
                .st1{fill:#D12A2C;}
                .chippy-eye {
                  animation: chippyBlink 3s ease-in-out infinite;
                  transform-origin: center;
                }
                .chippy-mouth {
                  transform-origin: 50% 50%;
                  transition: transform 0.5s ease-in-out;
                }
                .chippy-mouth.sad {
                  transform: rotate(180deg) translateY(-20px);
                }
                
                @keyframes chippyBlink {
                  0%, 90%, 100% { 
                    transform: scaleY(1);
                  }
                  95% { 
                    transform: scaleY(0.1);
                  }
                }
              `}</style>
            </defs>
          
          {/* Main body circles */}
          <circle cx="3641.8" cy="3641.8" r="3314.3"/>
          <circle className="st0" cx="3641.8" cy="3641.8" r="3069.1"/>
          
                                {/* All the decorative paths */}
           <path className="st1" d="M2460.9,1747.8c-4.9-27.3-24-133.4-122.3-191.7c-92.7-55-187.8-27.2-199-23.7c-95.8,29.7-140.1,108-147.9,122.8c-7.8-14.9-52-93.1-147.9-122.8c-11.2-3.5-106.3-31.3-199,23.7c-98.3,58.3-117.4,164.4-122.3,191.7c-10.4,57.7-2.9,134.7,84,275.7c60,97.3,174.9,252,385.2,394.3c210.3-142.3,325.2-297,385.2-394.3C2463.8,1882.5,2471.3,1805.4,2460.9,1747.8z"/>
           <path d="M5606.9,1880.1c-99.3-78.9-221.4-39.7-227.2-37.7c64.9-70.3,78.8-170,35.7-249.5c-57.1-105.3-182.3-116.2-189.6-116.7c-7.3,0.5-132.5,11.4-189.6,116.7c-43.1,79.5-29.2,179.2,35.7,249.5c-5.8-2-127.9-41.2-227.2,37.7c-67.9,54-98.2,141.2-77,222.5c25.3,96.7,118.1,165,217.8,166.9c101.9,2,167.9-66,178.4-77.2c2,28.2,1.3,67.9-11.3,113.1c-21.3,76.5-65.1,127.5-90.1,152.6c54.5-0.6,108.9-1.2,163.4-1.8c54.5,0.6,108.9,1.2,163.4,1.8c-25-25.1-68.8-76-90.1-152.6c-12.6-45.2-13.3-84.8-11.3-113.1c10.5,11.1,76.5,79.2,178.4,77.2c99.7-1.9,192.5-70.2,217.8-166.9C5705.1,2021.3,5674.8,1934.1,5606.9,1880.1z"/>
           <path d="M5591,5374.2c-25.1-81.8-69-118.7-208-263.1c-51.9-53.9-122.1-128-204.4-218.6c-82.3,90.6-152.5,164.7-204.4,218.6c-139,144.3-182.9,181.3-208,263.1c-10.6,34.5-51.4,167.5,28.4,256c49.6,54.9,132.1,77.7,206.2,60.4c74.4-17.3,116.1-69.3,128-85.3c-3.2,30.7-9.8,67.8-23.1,108.4c-20.9,63.8-50.5,112.5-74.7,145.8c49.2,0,98.4,0,147.5,0l0,0c49.2,0,98.4,0,147.5,0c-24.2-33.2-53.8-81.9-74.7-145.8c-13.3-40.7-19.9-77.8-23.1-108.4c11.9,16,53.6,68,128,85.3c74.1,17.3,156.6-5.5,206.2-60.4C5642.5,5541.7,5601.6,5408.7,5591,5374.2z"/>
          
                     {/* Regular decorative lines */}
           <g>
             <path d="M2709.7,1924.7c-33.3,0-65.4-18.1-81.7-49.7c-23.3-45.1-5.6-100.5,39.5-123.7c298.8-154.3,635.7-235.9,974.3-235.9c336.6,0,658.7,76.4,957.1,227.1c45.3,22.9,63.5,78.1,40.6,123.4s-78.1,63.4-123.4,40.6c-272.6-137.6-566.7-207.4-874.4-207.4c-313.8,0-613.3,72.5-890,215.4C2738.3,1921.4,2723.9,1924.7,2709.7,1924.7z"/>
           </g>
           <g>
             <path d="M5413.5,4734.5c-15.3,0-30.9-3.9-45.2-11.9c-44.1-25-59.7-81-34.7-125.2c164.1-289.9,250.9-620.4,250.9-955.7c0-315.9-77.5-629.5-224.2-906.8c-23.7-44.8-6.6-100.4,38.3-124.1c44.9-23.7,100.4-6.6,124.1,38.3c160.6,303.7,245.5,646.9,245.5,992.7c0,366.9-95,728.7-274.7,1046.1C5476.6,4717.8,5445.5,4734.5,5413.5,4734.5z"/>
           </g>
           <g>
             <path d="M3641.8,5768.2c-334.9,0-655.4-75.7-952.7-224.9c-45.3-22.8-63.6-77.9-40.9-123.3c22.8-45.3,77.9-63.6,123.3-40.9c271.5,136.3,564.3,205.4,870.3,205.4c298.3,0,584.3-65.8,850.3-195.5c45.6-22.3,100.6-3.3,122.8,42.3c22.2,45.6,3.3,100.6-42.3,122.8C4281.4,5696.2,3968.2,5768.2,3641.8,5768.2z"/>
           </g>
          <g>
            <path 
              style={{
                transformOrigin: '50% 50%',
                transition: 'transform 0.5s ease-in-out',
                transform: (streetAnalysis?.rating !== undefined && streetAnalysis.rating <= 4) ? 'rotate(180deg) translateY(-1500px)' : 'none'
              }}
              d="M3625.5,4933.4c-283.6,0-558-84.5-797.3-246.1c-257.5-173.9-449.4-420.3-555.1-712.6c-21.6-59.6,9.3-125.4,68.9-147c59.6-21.6,125.4,9.3,147,68.9c180,497.8,669.6,829.3,1190.4,806c493.2-22.1,931.6-356.3,1090.7-831.8c20.1-60.1,85.2-92.5,145.3-72.4c60.1,20.1,92.5,85.2,72.4,145.3c-93.2,278.4-265.7,519.5-499,697c-233.3,177.6-509.7,278.3-799.2,291.3C3668.2,4932.9,3646.8,4933.4,3625.5,4933.4z"
            />
          </g>
          
                     {/* Animated Eyes */}
           <ellipse className="chippy-eye" cx="2935.5" cy="3094.3" rx="260.7" ry="353.1"/>
           <ellipse className="chippy-eye" cx="4345.6" cy="3094.3" rx="260.7" ry="353.1"/>
          
          {/* Decorative elements */}
          <path className="st1" d="M3641.8,572.7c-244,0-481.3,28.6-708.8,82.4c45.6,212.5,91.2,425,136.8,637.6c9.3,43.2,51,71.3,94.5,63.5c133.3-23.9,294.4-41.9,477-39.9c176.7,1.9,332.7,22,462.4,47.4c43.6-8.5,85.9-19.2,95.6-62.5c48.4-215.9,96.9-431.7,145.3-647.6C4118.9,600.8,3883.6,572.7,3641.8,572.7z"/>
          <path className="st1" d="M656.8,4357.5c209.1-44.9,418.3-89.7,627.5-134.6c43.2-9.3,71.3-51,63.5-94.5c-23.9-133.3-41.9-294.4-39.9-477c1.9-176.7,22-332.7,47.4-462.4c8.5-43.6-19.2-85.9-62.5-95.6c-213.9-48-427.8-96-641.6-144c-51.3,222.5-78.4,454.3-78.4,692.4C572.7,3888.2,602,4127.8,656.8,4357.5z"/>
          <path className="st1" d="M3641.8,6710.8c242.9,0,479.2-28.4,705.8-81.7c-44.4-206.9-88.8-413.9-133.2-620.8c-9.3-43.2-51-71.3-94.5-63.5c-133.3,23.9-294.4,41.9-477,39.9c-176.7-1.9-332.7-22-462.4-47.4c-43.6-8.5-85.9,19.2-95.6,62.5c-47.2,210.4-94.4,420.8-141.6,631.1C3167.6,6683.2,3401.5,6710.8,3641.8,6710.8z"/>
          <path className="st1" d="M6630.8,2942.9c-210.3,45.1-420.6,90.2-631,135.4c-43.2,9.3-71.3,51-63.5,94.5c23.9,133.3,41.9,294.4,39.9,477c-1.9,176.7-22,332.7-47.4,462.4c-8.5,43.6,19.2,85.9,62.5,95.6c212.4,47.6,424.7,95.3,637,143c53.8-227.6,82.4-464.9,82.4-708.9z"/>
          <polygon className="st1" points="1601.9,5386.2 2008.9,4897.9 2415.9,5386.2 2008.9,5874.5 "/>
        </svg>
      </div>
      <div style={{
        fontSize: '10px',
        fontWeight: '900',
        color: '#D97706',
        marginTop: '2px',
        textAlign: 'center',
        lineHeight: '1',
        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
      }}>
        Chippy
      </div>
      {/* Chat button rendered inline with tabs; omit duplicate here */}
    </div>
    );
  };

  return (
    <div className="coaching-bubble" style={{ width: '100%' }}>
      <div className="coaching-bubble-content" style={{ width: '100%', maxWidth: 'none', fontSize: 16 }}>
        {/* Speech bubble arrow - only show when not in drawer */}
        {!isInDrawer && <div className="coaching-bubble-arrow"></div>}
        
        {/* Tabs */}
        {renderTabs()}
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Rating moved to header stripe above tabs to save space */}
          </div>
        </div>

        {/* Modular grid layout for analysis content */}
        {(() => {
          const moduleCardStyle: React.CSSProperties = {
            border: '1px solid #d1d5db',
            background: '#ffffff',
            borderRadius: 8,
            padding: 8,
            color: '#111827'
          };
          const moduleHeaderStyle: React.CSSProperties = {
            fontSize: 12,
            fontWeight: 800,
            color: '#374151',
            marginBottom: 6,
            textTransform: 'uppercase',
            letterSpacing: 0.4
          } as any;

          const hasOppRange = !!(streetAnalysis?.opponentRangeEstimation?.likelyHands && streetAnalysis.opponentRangeEstimation.likelyHands.length > 0);
          const hasGto = !!(streetAnalysis?.gtoOptimalPlay && streetAnalysis.gtoOptimalPlay.length > 0);
          const cp = street.toLowerCase() !== 'preflop' ? (streetAnalysis?.callProfitability || null) : null;
          const showQuick = cp && cp.considered !== false;
          const ratioStr = computePotOddsRatio() || (typeof cp?.potOddsRatio === 'string' ? (cp as any).potOddsRatio : '–');
          const needStr = formatPercent((cp as any)?.requiredEquityPct);
          const haveStr = formatPercent((cp && typeof cp.heroEquityPctUsed === 'number') ? cp.heroEquityPctUsed : (streetAnalysis as any)?.heroEquityPct);
          const profitable = (cp as any)?.profitable;
          const profitableColor = (profitable === false ? '#ef4444' : profitable === true ? '#10b981' : '#111827');
          const hasMistakes = !!(keyMistakes && keyMistakes.length);
          const tips = (streetAnalysis?.learningTips || []).slice(0, 3);

          return (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8, width: '100%' }}>
              {(hasOppRange || hasGto) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 8, gridColumn: '1 / -1' }}>
                  {hasOppRange && (
                    <div style={moduleCardStyle}>
                      <div style={moduleHeaderStyle}>Opponent Range</div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {streetAnalysis!.opponentRangeEstimation!.likelyHands!.map((hand, idx) => {
                          const raw = (hand || '').toString();
                          const rawLower = raw.replace(/\s+/g, '').toLowerCase();
                          const isShorthand = /^[2-9tjqka]{2}[so]?$/.test(rawLower);
                          const isExactTwoCards = /^[2-9tjqka][hdcs][2-9tjqka][hdcs]$/.test(rawLower);
                          if (isShorthand) {
                            const ranks = raw.replace(/\s+/g, '').slice(0, 2).toUpperCase();
                            const suffixRaw = raw.replace(/\s+/g, '').slice(2, 3);
                            const suffix = /[so]/i.test(suffixRaw) ? suffixRaw.toLowerCase() : '';
                            const display = `${ranks}${suffix}`;
                            return (
                              <span key={idx} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: '#ffffff', color: '#111827' }}>{display}</span>
                            );
                          }
                          if (isExactTwoCards) {
                            const cards = parseLikelyHand(raw);
                            return (
                              <span key={idx} style={{ display: 'flex', gap: 4 }}>
                                {cards.map((c, i) => {
                                  if (!c.valid) return <MiniFallbackCard key={i} label="X" />;
                                  if (c.valid && c.suitKnown && c.rank && c.suit) return <HandHistoryCard key={i} rank={c.rank.toUpperCase()} suit={c.suit} />;
                                  return <MiniFallbackCard key={i} label="X" />;
                                })}
                              </span>
                            );
                          }
                          return (
                            <span key={idx} style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, border: '1px solid #d1d5db', background: '#ffffff', color: '#111827' }}>{raw}</span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {hasGto && (
                    <div style={moduleCardStyle}>
                      <div style={moduleHeaderStyle}>GTO Mix</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
                        {streetAnalysis!.gtoOptimalPlay!
                          .slice()
                          .sort((a: any, b: any) => {
                            const af = typeof a?.frequency === 'number' ? a.frequency : parseFloat(String(a?.frequency));
                            const bf = typeof b?.frequency === 'number' ? b.frequency : parseFloat(String(b?.frequency));
                            const an = Number.isFinite(af) ? af : 0;
                            const bn = Number.isFinite(bf) ? bf : 0;
                            return bn - an; // descending
                          })
                          .slice(0, 4)
                          .map((e, i) => {
                          const rawFreq = (e as any)?.frequency;
                          const num = typeof rawFreq === 'number' ? rawFreq : parseFloat(String(rawFreq));
                          const clamped = Number.isFinite(num) ? Math.max(0, Math.min(100, num)) : 0;
                          const pctStr = Number.isFinite(num) ? `${(Math.round(num * 10) / 10).toString()}%` : '-';
                          return (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.action}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap' }}>{pctStr}</span>
                              </div>
                              <div style={{ height: 6, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{ width: `${clamped}%`, height: '100%', background: '#60a5fa' }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showQuick && (
                <div style={moduleCardStyle}>
                  <div style={moduleHeaderStyle}>Quick Stats</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 4, columnGap: 8, alignItems: 'center' }}>
                    {street.toLowerCase() !== 'river' && (
                      <>
                        <span style={{ fontSize: 11, color: '#374151' }}>Outs</span>
                        <span style={{ fontSize: 12, fontWeight: 800 }}>{
                          typeof streetAnalysis?.heroOuts === 'number' ? streetAnalysis!.heroOuts : '–'
                        }</span>
                      </>
                    )}
                    <span style={{ fontSize: 11, color: '#374151' }}>Equity</span>
                    <span style={{ fontSize: 12, fontWeight: 800 }}>{formatPercent((streetAnalysis as any)?.heroEquityPct as any)}</span>
                    <span style={{ fontSize: 11, color: '#374151' }}>Call profitable</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: profitableColor }}>{
                      profitable === true ? 'Yes' : profitable === false ? 'No' : '–'
                    }</span>
                    {(cp as any)?.considered && (
                      <span style={{ gridColumn: '1 / -1', fontSize: 11, color: '#6b7280' }}>{`(${ratioStr} | need ${needStr} | have ${haveStr})`}</span>
                    )}
                  </div>
                </div>
              )}

              {hasMistakes && (
                <div style={moduleCardStyle}>
                  <div style={moduleHeaderStyle}>Key Mistakes</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {keyMistakes!.filter(m => m.street.toLowerCase() === street.toLowerCase()).slice(0,3).map((m, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>{m.description}</li>
                    ))}
                  </ul>
                </div>
              )}

              {tips.length > 0 && (
                <div style={moduleCardStyle}>
                  <div style={moduleHeaderStyle}>Learning Tips</div>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {tips.map((t, i) => (
                      <li key={i} style={{ marginBottom: 4 }}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer: Centered large Chippy chat button */}
        {showChatButton && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
            <button
              type="button"
              aria-label="Chat with Chippy"
              title="Chat with Chippy"
              onClick={() => onChatWithCoach && onChatWithCoach()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChatWithCoach && onChatWithCoach();
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderRadius: 10,
                background: '#FFCE3E',
                border: '1px solid #D4A017',
                color: '#111827',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              <img src="/images/Chippy.svg" alt="Chippy" style={{ width: 20, height: 20, borderRadius: 10 }} />
              <span>Chat with Chippy</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoachingBubble; 